import type { ReactNode } from "react";
import { cn } from "./cn";

type Variant = "primary" | "secondary" | "ghost";
type Size = "sm" | "md" | "lg";

const sizeStyles: Record<Size, string> = {
  sm: "h-9 px-4 text-[13px]",
  md: "h-12 px-6 text-[15px]",
  lg: "h-14 px-8 text-[17px]",
};

const variantStyles: Record<Variant, string> = {
  primary: "bg-accent text-bg",
  secondary: "bg-transparent text-accent ring-1 ring-inset ring-accent",
  ghost: "bg-transparent text-td-1",
};

const baseClasses =
  "hex-clip relative font-mono font-semibold uppercase tracking-wider " +
  "cursor-pointer transition-[filter,transform] duration-200 " +
  "hover:brightness-110 hover:-translate-y-px " +
  "inline-flex items-center justify-center text-center " +
  "max-md:whitespace-normal max-md:leading-tight max-md:h-auto max-md:min-h-12 max-md:py-2.5";

function isExternal(href: string) {
  return /^(https?:|mailto:|tel:)/i.test(href);
}

type CommonProps = {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
  className?: string;
};

type HexButtonProps =
  | (CommonProps & { href: string; onClick?: never })
  | (CommonProps & { href?: never; onClick?: () => void });

export function HexButton(props: HexButtonProps) {
  const { children, variant = "ghost", size = "md", className } = props;
  const classes = cn(
    baseClasses,
    sizeStyles[size],
    variantStyles[variant],
    className,
  );

  if ("href" in props && props.href) {
    const external = isExternal(props.href);
    return (
      <a
        href={props.href}
        className={classes}
        {...(external && { target: "_blank", rel: "noopener noreferrer" })}
      >
        {children}
      </a>
    );
  }

  return (
    <button type="button" onClick={props.onClick} className={classes}>
      {children}
    </button>
  );
}
